import React from "react";
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import { observer } from "mobx-react-lite";
import MomentUtils from "@date-io/moment";
import moment from "moment"; // Import moment.js
import { FieldRenderProps } from "react-final-form";
import {
  DateTimeRenderer,
  PresentationalTimeComponentProps,
  Type,
  Validator
} from "lego-gui-lib";

export interface DatePickerProps extends FieldRenderProps<any> {
  clearable?: boolean;
  customDateFormat?: string;
  disableFuture?: boolean;
  error?: boolean;
  label?: string;
  locale?: any;
  minDate?: any;
  maxDate?: any;
  placeholder?: string;
  readOnly?: boolean;
  showTodayButton?: boolean;
  touched?: boolean;
}

/**
 * Date Picker component for Material-UI pickers using moment.js (compatible with React 16)
 */
export const DatePickerComponent = observer((props: DatePickerProps) => {
  const {
    input: { name, onChange, value },
    clearable,
    customDateFormat,
    disableFuture,
    error,
    label,
    locale,
    minDate,
    maxDate,
    placeholder,
    readOnly,
    showTodayButton,
    touched,
    ...rest
  } = props;

  const format = customDateFormat || "YYYY-MM-DD";

  // Handle date change, setting the day to the 1st at midnight UTC
  const handleDateChange = (date: any) => {
    if (date) {
      // Ensure the timestamp is set to the first of the Date at 00:00:00 UTC
      const formattedDate = moment
        .utc(date)
        .startOf("month")
        .format("YYYY-MM-DD");
      onChange(formattedDate);
    } else {
      onChange(null);
    }
  };

  return (
    <MuiPickersUtilsProvider utils={MomentUtils} locale={locale}>
      <DatePicker
        name={name}
        clearable={clearable}
        {...rest}
        views={["date"]} // Show only Year and Date
        format={format}
        error={error && touched}
        disabled={readOnly}
        disableFuture={disableFuture || false}
        fullWidth
        label={label}
        margin="dense"
        minDate={minDate}
        maxDate={maxDate}
        onChange={handleDateChange} // Use timestamp-based handler
        placeholder={placeholder || ""}
        readOnly={readOnly}
        showTodayButton={showTodayButton}
        inputVariant="outlined"
        value={
          value
            ? moment
                .utc(value)
                .startOf("month")
                .toDate()
            : null
        } // Ensure display is correct
      />
    </MuiPickersUtilsProvider>
  );
});

export type DateFieldProps =
  | Validator
  | (DatePickerProps & PresentationalTimeComponentProps);

export const CustomDateField = (
  props?:
    | Partial<DatePickerProps & PresentationalTimeComponentProps>
    | Validator,
  ...validators: Validator[]
) => {
  if (props instanceof Function) {
    validators.unshift(props);
  }

  return new Type({
    Component: DatePickerComponent,
    PresentationComponent: DateTimeRenderer,
    props: props as DatePickerProps & PresentationalTimeComponentProps, // Ensure proper type handling
    validators
  });
};
